import { useEffect, useRef } from 'react';
import {
  WarningCircleIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  CreditCardIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from 'src/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { useImportPayments } from 'src/api/rpc/hooks/rebates/useHistoricalPaymentImport';
import type { ImportPaymentsResponse } from '@erp/rpc';

interface SupplierMappingEntry {
  csvSupplierName: string;
  supplierId: string;
}

interface PaymentStep3ResultsProps {
  paymentsCsvBase64: string;
  supplierMappings: SupplierMappingEntry[];
  onComplete: () => void;
  onDone: () => void;
  onError: (error: string) => void;
  isLoading: boolean;
  error?: string;
  importComplete: boolean;
}

export default function PaymentStep3Results({
  paymentsCsvBase64,
  supplierMappings,
  onComplete,
  onDone,
  error,
  importComplete,
}: PaymentStep3ResultsProps) {
  const importMutation = useImportPayments();
  const result = importMutation.data as ImportPaymentsResponse | undefined;
  const hasStartedImport = useRef(false);

  // Trigger import on mount - only once
  useEffect(() => {
    if (!importComplete && !hasStartedImport.current) {
      hasStartedImport.current = true;
      importMutation.mutate({
        paymentsCsvBase64,
        supplierMappings,
      });
    }
  }, [importComplete, importMutation, paymentsCsvBase64, supplierMappings]);

  // Call onComplete when import succeeds
  useEffect(() => {
    if (result?.success && !importComplete) {
      onComplete();
    }
  }, [result, importComplete, onComplete]);

  // Loading state
  if (importMutation.isPending) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <CircleNotchIcon className="h-12 w-12 animate-spin text-primary" />
        <div className="text-center">
          <h2 className="text-lg font-semibold">Importing Payments...</h2>
          <p className="text-sm text-muted-foreground">
            This may take a moment. Please don&apos;t close this page.
          </p>
        </div>
      </div>
    );
  }

  // Error state (including validation errors from result)
  if (importMutation.isError || error) {
    return (
      <div className="space-y-6">
        <AlertContainer variant="destructive">
          <WarningCircleIcon className="h-4 w-4" />
          <AlertTitle>Import Failed</AlertTitle>
          <AlertDescription>
            {error ||
              importMutation.error?.message ||
              'An error occurred during import.'}
          </AlertDescription>
        </AlertContainer>
        <div className="flex justify-center">
          <Button onClick={onDone}>Back to Agreements</Button>
        </div>
      </div>
    );
  }

  // Validation errors from result (not successful)
  if (result && !result.success) {
    return (
      <div className="space-y-6">
        <AlertContainer variant="destructive">
          <WarningCircleIcon className="h-4 w-4" />
          <AlertTitle>Validation Failed</AlertTitle>
          <AlertDescription>
            Some suppliers don&apos;t have matching agreements. Please create
            historical agreements for these suppliers first.
          </AlertDescription>
        </AlertContainer>

        {result.validationIssues.length > 0 && (
          <div>
            <h3 className="text-sm font-medium mb-2">Issues</h3>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Supplier</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.validationIssues.map((issue, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">
                        {issue.type}
                      </TableCell>
                      <TableCell>{issue.message}</TableCell>
                      <TableCell>{issue.supplierName || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        <div className="flex justify-center">
          <Button onClick={onDone}>Back to Agreements</Button>
        </div>
      </div>
    );
  }

  // Success state
  if (result?.success) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 mb-4">
            <CheckCircleIcon className="h-8 w-8 text-green-500" />
          </div>
          <h2 className="text-xl font-semibold">Import Complete!</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Successfully imported payments
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="p-4 bg-green-500/10 rounded-lg text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <CreditCardIcon className="h-5 w-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold">{result.paymentsCreated}</p>
            <p className="text-sm text-muted-foreground">Payments Created</p>
          </div>
          <div className="p-4 bg-muted/30 rounded-lg text-center">
            <p className="text-2xl font-bold">
              ${result.totalPaymentAmount.toLocaleString()}
            </p>
            <p className="text-sm text-muted-foreground">Total Amount</p>
          </div>
          <div className="p-4 bg-muted/30 rounded-lg text-center">
            <p className="text-2xl font-bold">{result.bySupplier.length}</p>
            <p className="text-sm text-muted-foreground">Suppliers</p>
          </div>
        </div>

        {/* Skipped Stats */}
        {result.skippedPayments > 0 && (
          <AlertContainer>
            <WarningCircleIcon className="h-4 w-4" />
            <AlertTitle>Some Records Skipped</AlertTitle>
            <AlertDescription>
              {result.skippedPayments} payment(s) were skipped.
            </AlertDescription>
          </AlertContainer>
        )}

        {/* Warnings */}
        {result.warnings.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Import Warnings</h3>
            <div className="max-h-40 overflow-y-auto border rounded-lg p-3 bg-muted/30">
              {result.warnings.slice(0, 20).map((warning, idx) => (
                <p key={idx} className="text-sm text-muted-foreground">
                  {warning}
                </p>
              ))}
              {result.warnings.length > 20 && (
                <p className="text-sm text-muted-foreground">
                  ... and {result.warnings.length - 20} more
                </p>
              )}
            </div>
          </div>
        )}

        {/* Per-Supplier Breakdown */}
        {result.bySupplier.length > 0 && (
          <div>
            <h3 className="text-sm font-medium mb-2">By Supplier</h3>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Agreement</TableHead>
                    <TableHead className="text-right">Payments</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.bySupplier.map((supplier, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">
                        {supplier.supplierName}
                      </TableCell>
                      <TableCell
                        className="truncate max-w-[200px]"
                        title={supplier.agreementName}
                      >
                        {supplier.agreementName}
                      </TableCell>
                      <TableCell className="text-right">
                        {supplier.paymentsCreated}
                      </TableCell>
                      <TableCell className="text-right">
                        ${supplier.totalPaymentAmount.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Done Button */}
        <div className="flex justify-center pt-4 border-t">
          <Button onClick={onDone} size="lg">
            Done - Back to Agreements
          </Button>
        </div>
      </div>
    );
  }

  // Initial state (shouldn't normally show)
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <p className="text-muted-foreground">Preparing import...</p>
    </div>
  );
}
